// Lista de la venta rápida: Producto, Marca, Precio, Stock, Promoción (si alguna fila trae) y Cantidad.
// Clic en la fila agrega 1; la cantidad se ajusta en la celda sin pasar del stock.
import { Minus, Plus } from 'lucide-react';
import { CellLines } from '@/shared/patterns/DataTable';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import {
    POS_KBD,
    posColumns,
    PosProductTable,
    type PosColumn,
} from '../../pos/components/PosProductTable';
import { type PosSort, type PosSortKey } from '../../pos/lib/posCatalog';
import { hasPromo, type PosStat, type PosStats } from '../../pos/lib/posFilters';
import { maxQtyFor } from '@/domains/sales/wizard/cartLines';
import { isLastAccessories } from '../lib/accessoriesCatalog';
import type { CartQuantities } from '../lib/accessoriesCart';
import { QTY_BUTTON } from './AccessoriesCartDrawer';

const STAT_ITEMS: { key: PosStat; label: string; tone?: 'primary' | 'warning' }[] = [
    { key: 'all', label: 'Todos' },
    { key: 'promo', label: 'Con promo', tone: 'primary' },
    { key: 'last', label: 'Últimas piezas', tone: 'warning' },
];

/** El nombre de la fila: el modelo o, sin él, la descripción del SKU. */
function productName(item: CatalogItem): string {
    return item.model.trim() || itemDisplayName(item);
}

function QuantityCell({
    item,
    qty,
    onChangeQty,
}: {
    item: CatalogItem;
    qty: number;
    onChangeQty: (sku: string, delta: number) => void;
}) {
    const name = productName(item);
    const stop = (event: { stopPropagation: () => void }) => {
        event.stopPropagation();
    };
    if (qty === 0) {
        return (
            <button
                type="button"
                aria-label={`Agregar ${name}`}
                disabled={maxQtyFor(item) <= 0}
                onClick={(event) => {
                    stop(event);
                    onChangeQty(item.sku, 1);
                }}
                className="inline-flex items-center gap-1 rounded-[5px] bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
                <Plus className="size-3" aria-hidden />
                Agregar
            </button>
        );
    }
    return (
        <span className="inline-flex items-center gap-1">
            <button
                type="button"
                aria-label={`Quitar una unidad de ${name}`}
                onClick={(event) => {
                    stop(event);
                    onChangeQty(item.sku, -1);
                }}
                className={QTY_BUTTON}
            >
                <Minus className="size-3" aria-hidden />
            </button>
            <span className="min-w-[22px] text-center font-bold tabular-nums">{qty}</span>
            <button
                type="button"
                aria-label={`Agregar una unidad de ${name}`}
                disabled={qty >= maxQtyFor(item)}
                onClick={(event) => {
                    stop(event);
                    onChangeQty(item.sku, 1);
                }}
                className={QTY_BUTTON}
            >
                <Plus className="size-3" aria-hidden />
            </button>
        </span>
    );
}

export function AccessoriesTable({
    rows,
    stats,
    stat,
    onStatChange,
    query,
    sort,
    onSortToggle,
    cart,
    onChangeQty,
    isLoading,
    isError,
    onRetry,
}: {
    rows: CatalogItem[];
    stats: PosStats;
    stat: PosStat;
    onStatChange: (next: PosStat) => void;
    /** Búsqueda vigente, para decir con qué término no hubo resultados. */
    query: string;
    sort: PosSort;
    onSortToggle: (key: PosSortKey) => void;
    cart: CartQuantities;
    /** Suma o resta unidades; quien la recibe respeta el tope del stock. */
    onChangeQty: (sku: string, delta: number) => void;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
}) {
    const base = posColumns({ isLowStock: isLastAccessories });
    const trimmed = query.trim();
    const columns: PosColumn[] = [
        {
            key: 'product',
            label: 'Producto',
            sortKey: 'model',
            cellOverflow: 'wrap',
            cell: (item) => (
                <CellLines
                    primary={productName(item)}
                    primaryTitle={productName(item)}
                    primaryClassName="font-semibold"
                    secondary={item.sku}
                    secondaryClassName="font-mono"
                />
            ),
        },
        { ...base.brand, width: '120px' },
        base.price,
        base.stock,
        ...(rows.some(hasPromo) ? [base.promo] : []),
        {
            key: 'quantity',
            label: 'Cantidad',
            width: '112px',
            numeric: true,
            cellOverflow: 'nowrap',
            cell: (item) => (
                <QuantityCell item={item} qty={cart[item.sku] ?? 0} onChangeQty={onChangeQty} />
            ),
        },
    ];

    return (
        <PosProductTable
            rows={rows}
            columns={columns}
            stats={stats}
            statItems={STAT_ITEMS}
            stat={stat}
            onStatChange={onStatChange}
            sort={sort}
            onSortToggle={onSortToggle}
            onActivate={(item) => {
                onChangeQty(item.sku, 1);
            }}
            isRowSelected={(item) => (cart[item.sku] ?? 0) > 0}
            caption="Productos y servicios de la tienda"
            emptyText={
                trimmed === '' ? (
                    'Ningún producto coincide con los filtros.'
                ) : (
                    <>
                        Sin resultados para &quot;<strong>{trimmed}</strong>&quot;
                    </>
                )
            }
            hint={
                <>
                    <kbd className={POS_KBD}>Enter</kbd> agrega la 1ª fila
                </>
            }
            isLoading={isLoading}
            isError={isError}
            onRetry={onRetry}
        />
    );
}
